import { IntlShape } from 'react-intl';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { musicServicesByLinkType } from 'fym-shared/src/music-services/allMusicServices';
import { getWidgetTypeLabel } from '../smartLinkDetailsView/rail/getWidgetTypeLabel';
import { mediaBlockLabel } from '../widgets/views/mediaWidget/mediaBlockLabel';
import { presaveSubscribeBlockDetail } from '../widgets/views/presaveSubscribeWidget/presaveSubscribeServices';
import { mediaStatsMessages } from './media/mediaStatsMessages';

const MAX_TEXT_LENGTH = 32;

/**
 * Widget data is an opaque JSON blob on the wire, so a field the type declares
 * as a string can still be missing on a row written by an older version. Every
 * read here goes through this.
 */
function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/**
 * A readable name for one block in the "what fans tapped" breakdown.
 *
 * The stats carry only a block's uuid and type, so a page with several links
 * would show a column of rows all reading "Link". The page already holds the
 * blocks themselves, so the name comes from the block: its own label, the
 * service it points at, or the text it shows.
 */
export function describeBlock(
  intl: IntlShape,
  blockUUID: string,
  blockType: string,
  widgets: SerializedWidget[] | null | undefined
): string {
  const typeLabel = getWidgetTypeLabel(intl, blockType as SmartLinkWidgetType);
  const widget = widgets?.find((candidate) => candidate.uuid === blockUUID);
  if (widget === undefined) {
    // The block was deleted after the taps were recorded. The history stays.
    return typeLabel;
  }

  switch (widget.type) {
    case SmartLinkWidgetType.link:
      return readString(widget.data?.label) ?? musicServicesByLinkType[widget.data?.type]?.name ?? typeLabel;
    case SmartLinkWidgetType.player:
      return musicServicesByLinkType[widget.data?.musicServiceType]?.name ?? typeLabel;
    case SmartLinkWidgetType.info: {
      const text = readString(widget.data?.text);
      return text === null ? typeLabel : text.slice(0, MAX_TEXT_LENGTH);
    }
    case SmartLinkWidgetType.media:
      return intl.formatMessage(mediaStatsMessages.blockRow, { label: mediaBlockLabel(intl, widget.data) });
    // "Get every release · 3 services and email": one block per page, so the services tell it apart.
    case SmartLinkWidgetType.presaveSubscribe:
      return `${typeLabel} · ${presaveSubscribeBlockDetail(intl, widget.data?.services)}`;
    // Image, social links, profile and pre-save carry nothing worth showing
    // beyond their type: an image has no caption and a profile is one per page.
    case SmartLinkWidgetType.image:
    case SmartLinkWidgetType.socialLinks:
    case SmartLinkWidgetType.profile:
    case SmartLinkWidgetType.presave:
    default:
      return typeLabel;
  }
}
